"use client";

import { LocaleLink } from "@i18n/routing";
import type { EffectCatalogItem } from "@saas/shared/config/effects-catalog";
import { cn } from "@ui/lib";
import { useTranslations } from "next-intl";
import Image from "next/image";
import { useEffect, useRef, useState } from "react";

/**
 * 一张特效卡：封面 + 底部渐变上的标题，左上角 HOT / NEW，右上角 Video / Image。
 * /ai-effects 瀑布流按封面原比例排（masonry）；换特效弹窗和右栏 Templates tab 格子小、主要扫标题，
 * 统一 4:3（uniform）
 */
export function EffectCard({
	item,
	uniform = false,
	current = false,
	sizes,
	onClick,
}: {
	item: EffectCatalogItem;
	uniform?: boolean;
	/** 正在用的这个特效：描边，读屏念「当前」 */
	current?: boolean;
	sizes: string;
	onClick?: () => void;
}) {
	const t = useTranslations();
	const tTabs = useTranslations("assets.tabs");
	const title = t(item.titleKey);
	const videoRef = useRef<HTMLVideoElement>(null);
	const [hovered, setHovered] = useState(false);
	const [focused, setFocused] = useState(false);
	const [playing, setPlaying] = useState(false);
	const previewing =
		item.kind === "video" && !!item.videoUrl && (hovered || focused);

	useEffect(() => {
		setPlaying(false);
		const video = videoRef.current;
		if (!previewing || !video) {
			return;
		}
		void video.play().catch(() => {
			// Keep the poster visible if playback is unavailable.
		});
		return () => video.pause();
	}, [previewing, item.videoUrl]);

	return (
		<LocaleLink
			href={item.href}
			onClick={onClick}
			onMouseEnter={() => setHovered(true)}
			onMouseLeave={() => setHovered(false)}
			onFocus={() => setFocused(true)}
			onBlur={() => setFocused(false)}
			aria-current={current ? "page" : undefined}
			className={cn(
				"group relative block overflow-hidden rounded-xl bg-zinc-900 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-blue-500 focus-visible:ring-offset-2",
				uniform
					? "aspect-[4/3]"
					: item.kind === "image"
						? "aspect-[3/4]"
						: item.orientation === "portrait"
							? "aspect-[9/16]"
							: "aspect-video",
				current && "ring-2 ring-blue-500 ring-offset-2",
			)}
		>
			<Image
				src={uniform ? (item.icon ?? item.poster) : item.poster}
				alt=""
				fill
				sizes={sizes}
				className="object-cover transition-transform duration-500 group-hover:scale-105"
			/>
			{previewing && (
				<video
					ref={videoRef}
					src={item.videoUrl}
					muted
					loop
					playsInline
					preload="none"
					aria-hidden
					onPlaying={() => setPlaying(true)}
					onError={() => setPlaying(false)}
					className={cn(
						"pointer-events-none absolute inset-0 h-full w-full object-cover transition-opacity duration-200",
						playing ? "opacity-100" : "opacity-0",
					)}
				/>
			)}
			<span
				aria-hidden
				className="absolute inset-0 bg-gradient-to-b from-transparent from-50% to-black/75"
			/>
			{item.badge ? (
				<span
					className={cn(
						"absolute top-2 left-2 rounded px-1.5 py-0.5 font-bold text-[10px] text-white uppercase",
						item.badge === "hot" ? "bg-red-500" : "bg-emerald-500",
					)}
				>
					{item.badge}
				</span>
			) : null}
			<span className="absolute top-2 right-2 rounded bg-black/45 px-1.5 py-0.5 text-[10px] text-white">
				{tTabs(item.kind)}
			</span>
			<span className="absolute inset-x-2.5 bottom-2 font-semibold text-sm text-white leading-snug">
				{title}
			</span>
		</LocaleLink>
	);
}
